import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  PortfolioHistoryItem,
  PortfolioState,
  UniverseSnapshot,
} from '../api/types'
import { parsePositions } from '../api/types'
import { Badge } from '../components/ui/Badge'
import { ExternalLink } from '../components/ui/ExternalLink'
import { Panel } from '../components/ui/Panel'
import { EmptyState, QueryView } from '../components/ui/States'
import { CHAIN_HISTORY_START, explorerTokenUrl } from '../config'
import { fmtNum, fmtPct, fmtTime, fmtUsd } from '../lib/format'
import { buildTokenIndex, nameFor, symbolFor } from '../lib/tokens'

/** Series palette: lime first, orange second, then muted CRT shades. */
const SERIES_COLORS = [
  '#cfff04',
  '#ff6600',
  '#00ff00',
  '#9e9e9e',
  '#7a9903',
  '#b34700',
  '#6b6b6b',
  '#474546',
]

export function Portfolio() {
  const portfolio = useQuery(() => api.portfolioCurrent())
  const history = useQuery(() => api.portfolioHistory())
  const universe = useQuery(() => api.universeLatest())

  const snapshot: UniverseSnapshot | null =
    universe.state.kind === 'ready' ? universe.state.data.snapshot : null

  return (
    <div className="space-y-6">
      <Panel title="holdings">
        <QueryView state={portfolio.state} onRetry={portfolio.refresh}>
          {(p) =>
            p.state ? (
              <HoldingsTable state={p.state} universe={snapshot} />
            ) : (
              <EmptyState
                title="no positions"
                detail={`No portfolio state recorded yet — the agent has not completed a cycle. Chain history begins ${CHAIN_HISTORY_START}.`}
              />
            )
          }
        </QueryView>
      </Panel>

      <Panel title="allocation history">
        <QueryView state={history.state} onRetry={history.refresh}>
          {(h) => <AllocationArea items={h.items} universe={snapshot} />}
        </QueryView>
      </Panel>
    </div>
  )
}

function HoldingsTable({
  state,
  universe,
}: {
  state: PortfolioState
  universe: UniverseSnapshot | null
}) {
  const index = useMemo(() => buildTokenIndex(universe), [universe])
  const positions = parsePositions(state.positions)
  const posByAddr = new Map(positions.tokens.map((t) => [t.address.toLowerCase(), t]))
  const rows = Object.entries(state.weights)
    .filter(([, w]) => w > 0)
    .sort(([, a], [, b]) => b - a)
  const weightSum = rows.reduce((a, [, w]) => a + w, 0)
  const cashWeight = Math.max(0, 1 - weightSum)

  if (rows.length === 0 && positions.cashUsd === null) {
    return (
      <EmptyState
        title="no positions"
        detail={`No holdings recorded yet — the agent has not traded. Chain history begins ${CHAIN_HISTORY_START}.`}
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr>
            <th className="th">token</th>
            <th className="th text-right">qty</th>
            <th className="th text-right">price</th>
            <th className="th text-right">value</th>
            <th className="th text-right">weight</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([addr, w]) => {
            const pos = posByAddr.get(addr.toLowerCase())
            const value =
              typeof state.navUsd === 'number' ? state.navUsd * w : null
            return (
              <tr key={addr} className="hover:bg-crt-panel2">
                <td className="td">
                  <ExternalLink href={explorerTokenUrl(addr)}>
                    {symbolFor(index, addr)}
                  </ExternalLink>
                  <div className="text-[11px] text-crt-dim">
                    {nameFor(index, addr)}
                  </div>
                </td>
                <td className="td text-right">
                  {pos ? fmtNum(pos.qty) : '—'}
                </td>
                <td className="td text-right">
                  {pos ? fmtUsd(pos.priceUsd) : '—'}
                </td>
                <td className="td text-right text-crt-lime">
                  {fmtUsd(value)}
                </td>
                <td className="td text-right">{fmtPct(w)}</td>
              </tr>
            )
          })}
          <tr className="bg-crt-bg/60">
            <td className="td">
              <span className="text-crt-gray">USDG</span>
              <div className="text-[11px] text-crt-dim">cash buffer</div>
            </td>
            <td className="td text-right">—</td>
            <td className="td text-right">—</td>
            <td className="td text-right text-crt-gray">
              {fmtUsd(positions.cashUsd)}
            </td>
            <td className="td text-right">{fmtPct(cashWeight)}</td>
          </tr>
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-crt-dim">
        <span>as of {fmtTime(state.asOf)}</span>
        <Badge tone={state.status === 'live' ? 'pos' : 'dim'}>
          {state.status}
        </Badge>
        {state.aiSummary ? <span>// {state.aiSummary}</span> : null}
      </div>
    </div>
  )
}

function AllocationArea({
  items,
  universe,
}: {
  items: PortfolioHistoryItem[]
  universe: UniverseSnapshot | null
}) {
  const index = useMemo(() => buildTokenIndex(universe), [universe])
  const { rows, symbols } = useMemo(() => {
    // Union of addresses across all points; cash (USDG) rendered last as
    // the residual 1 - sum(weights).
    const set = new Set<string>()
    for (const p of items) {
      for (const a of Object.keys(p.weights)) set.add(a)
    }
    const addrs = [...set].sort()
    const syms = addrs.map((a) => symbolFor(index, a))
    const rws = items.map((p) => {
      const row: Record<string, number | string> = { t: p.asOf }
      let sum = 0
      addrs.forEach((a, i) => {
        const w = p.weights[a] ?? 0
        row[syms[i] ?? a] = w
        sum += w
      })
      row['USDG'] = Math.max(0, 1 - sum)
      return row
    })
    return { rows: rws, symbols: [...syms, 'USDG'] }
  }, [items, index])

  if (rows.length < 2) {
    return (
      <EmptyState
        title="no data yet"
        detail={`Allocation history appears after at least two recorded snapshots. Chain history begins ${CHAIN_HISTORY_START}.`}
      />
    )
  }

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} stackOffset="expand">
          <CartesianGrid stroke="#474546" strokeDasharray="3 3" />
          <XAxis
            dataKey="t"
            stroke="#9e9e9e"
            tick={{ fontSize: 10 }}
            tickFormatter={(t: number) =>
              new Date(t).toISOString().slice(5, 10)
            }
          />
          <YAxis
            stroke="#9e9e9e"
            tick={{ fontSize: 10 }}
            tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
          />
          <Tooltip
            formatter={(v) => fmtPct(Number(v))}
            labelFormatter={(t) => new Date(Number(t)).toISOString().slice(0, 10)}
            contentStyle={{
              background: '#1c1c1c',
              border: '1px solid rgba(207,255,4,0.35)',
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {symbols.map((s, i) => (
            <Area
              key={s}
              type="monotone"
              dataKey={s}
              stackId="alloc"
              stroke={
                s === 'USDG'
                  ? '#474546'
                  : SERIES_COLORS[i % SERIES_COLORS.length]
              }
              fill={
                s === 'USDG'
                  ? '#474546'
                  : SERIES_COLORS[i % SERIES_COLORS.length]
              }
              fillOpacity={0.55}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
